import { useState } from "react";

/**
 * Validates a Guatemalan NIT (Número de Identificación Tributaria) format
 * @param {string} nit - The NIT string to validate
 * @returns {boolean} - Returns true if the NIT is valid, false otherwise
 */
export function isValidNIT(nit) {
  return true;
  if (!nit || nit === "C/F" || nit === "CF" || nit === "cf") return true;
  
  // Remove spaces
  const cleanNIT = nit.replace(/\s/g, '');

  // NIT should be between 8 and 12 characters (including verification digit)
  if (cleanNIT.length < 8 || cleanNIT.length > 12) {
    return false;
  }

  let baseNumber, verificationDigit;

  // Check if NIT contains a dash
  if (cleanNIT.includes('-')) {
    // Format with dash: Split at the dash
    const parts = cleanNIT.split('-');
    if (parts.length !== 2) return false;
    
    baseNumber = parts[0];
    verificationDigit = parts[1].toUpperCase();

    // Verify the parts after split
    if (!/^\d+$/.test(baseNumber) || !/^[0-9K]$/.test(verificationDigit)) {
      return false;
    }
  } else {
    // Format without dash: Use last character as verification digit
    if (!/^\d+[kK]?$/.test(cleanNIT)) {
      return false;
    }
    
    verificationDigit = cleanNIT.slice(-1).toUpperCase();
    baseNumber = cleanNIT.slice(0, -1);
  }

  // Calculate verification digit
  let sum = 0;
  for (let i = 0; i < baseNumber.length; i++) {
    const digit = parseInt(baseNumber[baseNumber.length - 1 - i]);
    sum += digit * (baseNumber.length - i + 2);
  }
  
  const mod = sum % 11;
  const calculatedVerificationDigit = mod === 10 ? 'K' : mod.toString();
  
  // Compare calculated verification digit with the actual one
  return verificationDigit === calculatedVerificationDigit;
}

/**
 * Validates if a string contains only ISO-8859-1 compatible characters
 * @param {string} text - The text to validate
 * @returns {boolean} - Returns true if the text is valid ISO-8859-1
 */
const isValidISO8859 = (text) => {
  try {
    const decoded = decodeURIComponent(escape(text));
    return decoded === text;
  } catch (e) {
    return false;
  }
};

/**
 * Higher-order function that adds ISO-8859-1 validation to an event handler
 * @param {Function} handler - The original event handler
 * @param {Object} options - Configuration options
 * @param {Function} options.onError - Custom error handler
 * @param {Array<string>} options.skipFields - Array of field IDs to skip validation
 * @param {Function} options.customHandler - Special handler for specific fields
 * @returns {Function} - Enhanced event handler with ISO-8859-1 validation
 */
export const withISO8859Validation = (
  handler,
  {
    onError = (msg) => console.error(msg),
    skipFields = [],
    customHandler = null
  } = {}
) => (event) => {
  const value = event.target.value;
  const fieldId = event.target.id;

  // Skip validation for non-text fields
  if (event.target.type === 'checkbox' || event.target.type === 'select-one') {
    return handler(event);
  }

  // Skip validation for specified fields
  if (skipFields.includes(fieldId)) {
    return handler(event);
  }

  // Use custom handler if provided and field matches
  if (customHandler && customHandler.fields?.includes(fieldId)) {
    return customHandler.handler(event);
  }

  if (value && !isValidISO8859(value)) {
    event.preventDefault();
    onError('Por favor ingresa solo caracteres válidos (letras, números y símbolos básicos)');
    return;
  }

  return handler(event);
};

export function useNITValidation(initialValue = '') {
  const [nit, setNIT] = useState(initialValue);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState('');

  const validateNIT = (value) => {
    const valid = isValidNIT(value);
    setIsValid(valid);
    setError(valid ? '' : 'NIT inválido. Por favor verifica el formato.');
    return valid;
  };

  const handleNITChange = (event) => {
    const value = event.target.value;
    setNIT(value);
    validateNIT(value);
  };

  return {
    nit,
    setNIT,
    isValid,
    error,
    validateNIT,
    handleNITChange
  };
}

export function useFormFields(initialState) {
  const [fields, setValues] = useState(initialState);

  return [
    fields,
    function(event) {
      //console.log(event.target.type, event.target.name)      
      if (event.target.type === "radio") {
        console.log(event.target.name + '('+event.target.id+')' + '->' + event.target.value)
        setValues({
          ...fields,
          [event.target.name]: event.target.value,
          [event.target.id]: event.target.value
        });
      } else if (event.target.type === "checkbox") {
        console.log(event.target.name + '('+event.target.id+')' + '->' + event.target.checked)
        setValues({
          ...fields,
          [event.target.name]: event.target.checked,
          [event.target.id]: event.target.checked
        });
      }else {
        // console.log(event.target.id + '->' + event.target.value)
        setValues({
          ...fields,
          [event.target.id]: event.target.value
        });
      }
    }
  ];
}